/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { cloneElement, VNode } from 'preact';
import { TransitionProps } from './Transition';

export type ChildKey = number | string;
export type ChildMapping = Map<string | number, VNode<TransitionProps>>;
export type ChildrenRecord<T = any> = {
  [Key in number | string | any]: T[];
};

/**
 * @classdesc
 * A utility class consisting of helper functions for handling transitions
 * related operations.
 */
export class TransitionUtils {
  /**
   * Creates a map of the children array with the calculated in prop
   *
   * @param children The newly received children
   * @param prevChildMapping The previous child mapping
   * @returns the newly created child mapping
   */
  public static getChildMapping(
    children: VNode<TransitionProps>[],
    prevChildMapping: ChildMapping = new Map(),
    onExited: (child: VNode<TransitionProps>, node: Element, key?: ChildKey) => void = () => {}
  ): ChildMapping {
    // A symbol to store trailing children
    const TRAILING = Symbol();
    let mappedDeletions = {} as ChildrenRecord;

    if (prevChildMapping.size !== 0) {
      // If previous children exists, get the mapped deleted children
      mappedDeletions = TransitionUtils._getMappedDeletions(children, prevChildMapping, TRAILING);
    }

    // Create a new Map with the new children along with the deletions inserted in their
    // respective positions
    const mergedChildrenMap: ChildMapping = children.reduce<ChildMapping>(
      (accumulator, currentChild) => {
        if (mappedDeletions[currentChild.key]) {
          // There are keys from prev that are deleted before the current
          // next key, so add them first
          const deletedChildren = mappedDeletions[currentChild.key];
          for (const key of deletedChildren) {
            const previousChild = prevChildMapping.get(key);
            // Set the in property to false, as this is children is removed
            accumulator.set(key, cloneElement(previousChild!, { in: false }));
          }
          // Then add the current key. Do not change the 'in' or 'onExited' properties as this is a
          // retained child.
          const previousChild = prevChildMapping.get(currentChild.key);
          accumulator.set(
            currentChild.key,
            cloneElement(currentChild, {
              onExited: previousChild!.props.onExited,
              in: previousChild!.props.in
            })
          );
        } else {
          // This is a new children. Set the in property to true
          const newChild = cloneElement(currentChild, {
            // bind the original child so that the original callbacks can be
            // called in the onExited callback from the argument.
            onExited: onExited.bind(null, currentChild),
            in: true
          });
          accumulator.set(currentChild.key, newChild);
        }

        return accumulator;
      },
      new Map() as ChildMapping
    );

    // Finally add any trailing deleted children present in the mappedDeletions[TRAILING]
    for (const key of mappedDeletions[TRAILING as any] || []) {
      const previousChild = prevChildMapping.get(key);
      // Set the in property to false, as this is children is removed
      mergedChildrenMap.set(key, cloneElement(previousChild!, { in: false }));
    }

    // Finally return the merged children map
    return mergedChildrenMap;
  }

  ////////////////////////////
  // Private helper methods //
  ////////////////////////////

  /**
   * Creates a map of deleted children wrt to the keys in the new data.
   *
   * @param children The newly received children
   * @param prevChildMapping The previous child mapping
   * @param TRAILING A unique symbol to be used for storing the trailing children
   * @returns A map containing deleted children
   */
  private static _getMappedDeletions(
    children: VNode<TransitionProps>[],
    prevChildMapping: ChildMapping,
    TRAILING: symbol
  ): ChildrenRecord<ChildKey> {
    // Create a set with keys of next children
    const nextChildrenKeys = new Set(children.map((children) => children.key));

    return [...prevChildMapping.keys()].reduce<ChildrenRecord<ChildKey>>(
      (accumulator, currentKey) => {
        if (nextChildrenKeys.has(currentKey)) {
          // We have reached a point where the closest prevKey that
          // is in the next, so if there are any pending keys add them
          // to this key in mappedDeletions so that the pending keys will
          // be added before the current next key
          accumulator[currentKey] = accumulator[TRAILING as any];
          delete accumulator[TRAILING as any];
        } else {
          // If key is not found in next, then add it to the trailing keys.
          const trailingChildren = accumulator[TRAILING as any]
            ? [...accumulator[TRAILING as any], currentKey]
            : [currentKey];
          accumulator[TRAILING as any] = trailingChildren;
        }

        return accumulator;
      },
      {} as ChildrenRecord<ChildKey>
    );
  }
}
